import {useLocation, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {notifications} from "@mantine/notifications";
import {
  DVIRDetailsContent,
  DVIRReviewFormDefaultValues,
  DVIRReviewFormWithSignature,
  DVIRReviewFormWithSignatureValues,
  DVIRTrailerInspectionContent,
  DVIRVehicleInspectionContent,
  ErrorPage,
  LoadingPage,
  ThemedPage,
  useDVIRMutation,
} from "@ots-frontend/ots-ui";
import {Card, Center, CloseButton, Container, Grid, Group, Paper, Space, Title} from "@mantine/core";
import {DVIRMechanicReview, dvirSelector, useGetDVIRQuery} from "@ots-frontend/ots-data";

type DVIRReviewFormPageProps = {
  id?: string;
  formDefaults?: DVIRReviewFormDefaultValues
}

export const DVIRReviewFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const formDefaults = state?.formDefaults;
  return <DVIRReviewFormPage id={id} formDefaults={formDefaults}/>;
};

export const DVIRReviewFormPage = (props: DVIRReviewFormPageProps) => {
  const {id, formDefaults} = props;

  const navigate = useNavigate();
  const {t} = useTranslation(["common"])

  const selectDvir = useMemo(() => {
    return dvirSelector()
  }, [])

  const {dvir, isFetching} = useGetDVIRQuery(id ?? skipToken, {
    selectFromResult: (result) => ({
      dvir: selectDvir(result),
      isFetching: result.isFetching
    })
  });

  const {
    updateDVIRMechanicReview,
    isUpdating,
  } = useDVIRMutation()

  const onSubmit = useCallback((values: DVIRReviewFormWithSignatureValues) => {
    if (!dvir) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "DVIR Not Found"
      });
      return;
    }

    const formValues = values.form

    const review: DVIRMechanicReview = {
      mechanicSignatureBase64: values.signature.dataUrl.split(",")[1],
      defectsCorrected: formValues.defectsCorrected,
      defectsNeedNotCorrected: formValues.defectsNeedNotCorrected,
      defectsComments: formValues.comments,
    }

    updateDVIRMechanicReview(dvir.id, review, {onSuccess: () => navigate(-1)})
  }, [dvir, updateDVIRMechanicReview, navigate]);


  if (isFetching) {
    return (
      <LoadingPage/>
    )
  }

  if (!dvir) {
    return (
      <ErrorPage message={"DVIR Not Found"}/>
    );
  }

  if (!dvir.requiresMechanicReview) {
    return (
      <ErrorPage message={"This DVIR does not require a mechanic review"}/>
    );
  }

  if (!dvir.isUnresolved) {
    return (
      <ErrorPage message={"This DVIR is already resolved"}/>
    );
  }

  return (
    <ThemedPage>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            <Paper w="100%" shadow="sm" p="md" py="md">
              <Group position="apart">
                <Title align="center">
                  {t("common.page.entity.dvir.review.form", "DVIR Mechanic Review")}
                </Title>
                <CloseButton title="Close" size="xl" iconSize={20} onSubmit={() => navigate(-1)}/>
              </Group>
              <Space h="md"/>
              <Grid>
                <Grid.Col span={8}>
                  <Container fluid={true}>
                    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                      <DVIRReviewFormWithSignature
                        defaultValues={formDefaults}
                        submitting={isUpdating}
                        onSubmit={onSubmit}/>
                    </Card>
                  </Container>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Card
                    shadow="sm"
                    padding="lg"
                    mb="lg"
                    radius="md"
                    withBorder>

                    <DVIRDetailsContent dvir={dvir}/>

                    <Space h="md" />

                    <DVIRVehicleInspectionContent dvir={dvir}/>

                    <Space h="md" />

                    <DVIRTrailerInspectionContent dvir={dvir}/>

                  </Card>
                </Grid.Col>
              </Grid>
            </Paper>
          </Center>
        </Container>
      )}
    </ThemedPage>
  );
};
